/**
 * Client Identity Management
 * 
 * Generates and persists a stable clientId for cross-device notification delivery.
 * Each device/browser gets a unique clientId that persists across sessions.
 * 
 * This enables the backend to track which events have been delivered to which clients,
 * allowing proper fan-out of notifications to all logged-in devices.
 */

import { v4 as uuidv4 } from 'uuid';

const CLIENT_ID_KEY = 'signalbridge_client_id';
const CLIENT_PLATFORM_KEY = 'signalbridge_client_platform';

/**
 * Detect current platform
 */
function detectPlatform() {
  // Check if running in Electron
  if (window.electronAPI) {
    return 'electron';
  }
  
  // Check if running as PWA
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'pwa';
  }
  
  // Default to web
  return 'web';
}

/**
 * Get or create a stable clientId for this device/browser
 * 
 * @returns {string} Stable clientId (format: platform-uuid)
 */
export function getOrCreateClientId() {
  try {
    // Try to retrieve existing clientId
    let clientId = localStorage.getItem(CLIENT_ID_KEY);
    let platform = localStorage.getItem(CLIENT_PLATFORM_KEY);
    
    // If no clientId exists, generate a new one
    if (!clientId) {
      platform = detectPlatform();
      const uuid = uuidv4();
      clientId = `${platform}-${uuid}`;
      
      // Persist to localStorage
      localStorage.setItem(CLIENT_ID_KEY, clientId);
      localStorage.setItem(CLIENT_PLATFORM_KEY, platform);
      
      console.log('[CLIENT-ID] Generated new clientId', {
        clientId,
        platform,
        timestamp: new Date().toISOString()
      });
    } else {
      console.log('[CLIENT-ID] Retrieved existing clientId', {
        clientId,
        platform: platform || 'unknown'
      });
    }
    
    return clientId;
  } catch (error) {
    console.error('[CLIENT-ID] Error managing clientId:', error);
    // Fallback to session-based ID if localStorage fails
    const fallbackId = `${detectPlatform()}-session-${Date.now()}`;
    console.warn('[CLIENT-ID] Using fallback session ID:', fallbackId);
    return fallbackId;
  }
}

/**
 * Get current platform
 * 
 * @returns {string} Platform identifier (electron|pwa|web)
 */
export function getClientPlatform() {
  try {
    const platform = localStorage.getItem(CLIENT_PLATFORM_KEY);
    return platform || detectPlatform();
  } catch (error) {
    return detectPlatform();
  }
}

/**
 * Get client metadata for logging/debugging
 * 
 * @returns {Object} Client metadata
 */
export function getClientMetadata() {
  return {
    clientId: getOrCreateClientId(),
    platform: getClientPlatform(),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };
}

/**
 * Reset clientId (for testing/debugging only)
 * WARNING: This will cause the client to be treated as a new device
 */
export function resetClientId() {
  try {
    localStorage.removeItem(CLIENT_ID_KEY);
    localStorage.removeItem(CLIENT_PLATFORM_KEY);
    console.warn('[CLIENT-ID] ClientId reset - will generate new ID on next call');
  } catch (error) {
    console.error('[CLIENT-ID] Error resetting clientId:', error);
  }
}
